import React from 'react'
import type { EditorPublishSettings, Audience } from '../../types/editor'

type Props = {
  open: boolean
  onClose: () => void
  settings: EditorPublishSettings
  onChange: (next: Partial<EditorPublishSettings>) => void
  urlBase?: string // e.g., window.location.origin + '/p/' preview base
  computedTitleSlug?: string // fallback when urlSlug empty
}

export default function PublishSettingsDrawer({ open, onClose, settings, onChange, urlBase = '', computedTitleSlug = '' }: Props) {
  const sectionCls = 'space-y-2'
  const labelCls = 'text-sm text-white/70'
  const inputCls = 'w-full rounded bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand'
  const toggleCls = 'inline-flex items-center gap-2 text-sm'

  const audienceOptions: { key: Audience; label: string }[] = [
    { key: 'everyone', label: 'Everyone' },
    { key: 'free', label: 'Free subscribers' },
    { key: 'paid', label: 'Paid subscribers' },
  ]

  const slug = (settings.urlSlug || computedTitleSlug || '').trim()
  const urlPreview = slug ? `${urlBase}${slug}` : `${urlBase}`

  return (
    <div aria-hidden={!open}>
      <div className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`} onClick={onClose} />
      <div role="dialog" aria-modal="true" className={`fixed right-0 top-0 h-full w-[380px] md:w-[440px] bg-neutral-900 border-l border-white/10 shadow-2xl z-50 transform transition-transform ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="text-base font-extrabold">Publishing</div>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white">✕</button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Status & Schedule */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Status</div>
            <div className="flex items-center gap-3">
              {(['publish', 'draft'] as const).map((s) => (
                <label key={s} className={toggleCls}>
                  <input type="radio" name="pub-status" checked={settings.status === s} onChange={() => onChange({ status: s })} />
                  <span className="capitalize">{s}</span>
                </label>
              ))}
            </div>
            <div className={sectionCls}>
              <div className={labelCls}>Schedule sending</div>
              <input type="datetime-local" value={settings.scheduleAt || ''} onChange={(e)=> onChange({ scheduleAt: e.target.value })} className={inputCls} />
              <div className="text-xs text-white/50">Leave empty to send immediately.</div>
            </div>
          </div>

          {/* Audience */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Audience</div>
            <div className="flex flex-wrap gap-2">
              {audienceOptions.map((opt) => (
                <button key={opt.key} onClick={() => onChange({ audience: opt.key })} className={`px-3 py-1.5 rounded-full border text-xs ${settings.audience === opt.key ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200' : 'bg-white/5 border-white/10 text-white/85 hover:bg-white/10'}`}>{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Email */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Email</div>
            <div>
              <div className={labelCls}>Subject line</div>
              <input value={settings.emailSubject} onChange={(e)=> onChange({ emailSubject: e.target.value })} className={inputCls} placeholder="Write a great subject…" />
            </div>
          </div>

          {/* Social share */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Share to X (Twitter)</div>
            <label className={toggleCls}>
              <input type="checkbox" checked={settings.shareToTwitter} onChange={(e)=> onChange({ shareToTwitter: e.target.checked })} />
              <span>Enable tweet sharing</span>
            </label>
            {settings.shareToTwitter && (
              <textarea value={settings.tweetText} onChange={(e)=> onChange({ tweetText: e.target.value })} rows={3} className={inputCls} placeholder="Optional tweet preview…" />
            )}
          </div>

          {/* URL & Canonical */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Link</div>
            <div>
              <div className={labelCls}>Post URL slug</div>
              <input value={settings.urlSlug} onChange={(e)=> onChange({ urlSlug: e.target.value })} className={inputCls} placeholder={computedTitleSlug || 'auto-from-title'} />
              {urlBase && <div className="mt-1 text-xs text-white/60">Preview: {urlPreview || urlBase}</div>}
            </div>
            <div>
              <div className={labelCls}>Canonical link</div>
              <input value={settings.canonicalUrl} onChange={(e)=> onChange({ canonicalUrl: e.target.value })} className={inputCls} placeholder="https://example.com/original-post" />
            </div>
          </div>

          {/* Comments & Notes */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Comments & Notes</div>
            <label className={toggleCls}>
              <input type="checkbox" checked={settings.commentsEnabled} onChange={(e)=> onChange({ commentsEnabled: e.target.checked })} />
              <span>Enable comments</span>
            </label>
            <label className={toggleCls}>
              <input type="checkbox" checked={settings.notesEnabled} onChange={(e)=> onChange({ notesEnabled: e.target.checked })} />
              <span>Enable Notes</span>
            </label>
            <label className={toggleCls}>
              <input type="checkbox" checked={settings.commentsSubscriberOnly} onChange={(e)=> onChange({ commentsSubscriberOnly: e.target.checked })} disabled={!settings.commentsEnabled} />
              <span>Subscriber-only comments</span>
            </label>
          </div>

          {/* Cover extras */}
          <div className={sectionCls}>
            <div className="text-sm font-semibold">Cover image</div>
            <div>
              <div className={labelCls}>Alt text</div>
              <input value={settings.coverAlt} onChange={(e)=> onChange({ coverAlt: e.target.value })} className={inputCls} placeholder="Describe the cover for readers and SEO" />
            </div>
            <label className={toggleCls}>
              <input type="checkbox" checked={settings.autoCropLandscape} onChange={(e)=> onChange({ autoCropLandscape: e.target.checked })} />
              <span>Auto-generate missing landscape</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}
